// Basic
import { useEffect } from 'react';
// Libs
import { useForm } from 'react-hook-form';
import { Navigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
// Store & actions
import actionsPreLogin from '../../api/actionsPreLogin';
// Components
import Entity from '@/common-utils/components/renders/entity/Entity';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage
} from '@/common-utils/components/ui/form';
import PasswordValidation from '@/common-utils/components/renders/passwordValidation/PasswordValidation';
import { Input } from '@/common-utils/components/ui/input';
import { Button } from '@/common-utils/components/ui/button';
// Common
import ICONS from '@/common-utils/enums/icons';
import RouteUrls from '@/app/root/enums/RouteUrls';
import { usePasswordValidation } from '@/common-utils/hooks/usePasswordValidation';
import { useToast } from '@/common-utils/hooks/use-toast';
// Lang
import { nsErrors, nsLogin, nsPreLogin } from '@/app/i18n/namespaces';

/**
 * @param root0
 * @param root0.token
 * @returns {JSX} PreLoginForm
 */
function PreLoginForm({ token }) {

    // Toast
    const { toast } = useToast();

    // Lang
    const { t: translate, i18n } = useTranslation();

    // Password Validation
    const {
        passwordSchema,
        status: passwordStatus,
        validate: validatePassword
    } = usePasswordValidation({
        translate: translate,
        language: i18n.language,
        minLength: 12
    });

    // Form
    const formSchema = z.object({
        email: z.string().email({ message: translate('form.validation.email.invalid') }),
        newPassword: passwordSchema,
        confirmPassword: z.string()
    }).refine((data) => data.newPassword === data.confirmPassword, {
        message: '',
        path: ['confirmPassword']
    });

    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: {
            email: token?.extra?.email ?? '',
            newPassword: '',
            confirmPassword: ''
        }
    });

    // API Call
    const onSuccess = () => {
        toast({
            description: <Entity namespace={nsPreLogin} entity='success' />,
            variant: 'success'
        });
    };

    const onError = (error) => {
        const errorCode = error?.response?.data?.errorCode ?? error?.code ?? 'UNKNOWN_ERROR';
        toast({
            description: <Entity namespace={nsErrors} entity={`api.${errorCode}`} />,
            variant: 'danger'
        });
    };

    const submitUpdateToken = actionsPreLogin.updateToken(onSuccess, onError);

    // Hooks
    useEffect(() => {
        form.trigger('email');
    }, [i18n.language]);

    // Submit
    const onSubmit = (values) => {
        const tokenUpdated = {
            token: token.uuid,
            action: token.type[0],
            value: values.confirmPassword
        };
        submitUpdateToken.mutate(tokenUpdated);
    };

    if (submitUpdateToken.isSuccess) {
        return <Navigate to={RouteUrls.LOGIN.url} />;
    }

    // Watch values
    const newPassword = form.watch('newPassword');
    const confirmPassword = form.watch('confirmPassword');

    const passwordsTyped = newPassword && confirmPassword;
    const passwordsMatch = passwordsTyped && newPassword === confirmPassword;
    const passwordsMismatch = passwordsTyped && !passwordsMatch;

    // Functions
    const confirmPasswordsClassName = () => {
        let className = 'italic text-sm font-extralight text-black';
        if (passwordsTyped) {
            if (passwordsMatch) { className = 'italic text-sm font-medium text-success-foreground'; }
            else { className = 'italic text-sm font-medium text-danger-foreground'; }
        }
        return className;
    };

    return (
        <div className='grid mt-2 mx-auto w-full max-w-md'>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className='flex flex-col space-y-4'>
                    <FormField
                        control={form.control}
                        name='email'
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel required>
                                    <Entity namespace={nsPreLogin} entity='email' />
                                </FormLabel>
                                <FormControl>
                                    <Input type='email' namespace={nsLogin} entity='email' {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name='newPassword'
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel required>
                                    <Entity namespace={nsPreLogin} entity='choose_pass' />
                                </FormLabel>
                                <PasswordValidation validationStatus={passwordStatus} />
                                <FormControl>
                                    <Input
                                        type='password'
                                        value={field.value}
                                        autoComplete='new-password'
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            validatePassword(value);
                                            field.onChange(value);
                                        }}
                                    />
                                </FormControl>
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name='confirmPassword'
                        render={({ field }) => {
                            let validationEntity = '';
                            if (passwordsMatch) { validationEntity = 'passwords_match'; }
                            else if (passwordsMismatch) { validationEntity = 'passwords_mismatch'; }

                            return (
                                <FormItem>
                                    <FormLabel required>
                                        <Entity namespace={nsPreLogin} entity='confirm_pass' />
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            type='password'
                                            value={field.value}
                                            autoComplete='new-password'
                                            onChange={(e) => field.onChange(e.target.value)}
                                        />
                                    </FormControl>
                                    <Entity
                                        className={confirmPasswordsClassName()}
                                        namespace={nsPreLogin}
                                        entity={validationEntity}
                                    />
                                </FormItem>
                            );
                        }}
                    />

                    <div className='text-center'>
                        <Button
                            type='submit'
                            variant='primary'
                            entity={'button.submit'}
                            size='lg'
                            icon={ICONS.VALIDATE}
                            disabled={!form.formState.isValid || submitUpdateToken.isLoading}
                        />
                    </div>

                </form>
            </Form>
        </div>
    );
}

PreLoginForm.propTypes = {
    token: PropTypes.object.isRequired
};

export default PreLoginForm;